import { LayoutExportSchema, ProfileExportSchema } from '@/types/schemas/workspace'
import type {
  LayoutExport,
  ProfileExport,
  ShareScope,
  WorkspaceDocument,
} from '@/types/schemas/workspace'
import type { BacktestStrategyExport } from '@/features/backtest/utils/strategy-export'
import { usersClient } from '@/services/api/api-client'

// ─── Compress / Decompress (browser CompressionStream) ───

const MAX_EXPANDED_BYTES = 4 * 1024 * 1024
const MAX_COMPRESSED_BYTES = MAX_EXPANDED_BYTES + 64 * 1024
const MAX_ENCODED_LENGTH = Math.ceil((MAX_COMPRESSED_BYTES * 4) / 3) + 4
const SHARE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{22}$/

function byteStream(data: Uint8Array): ReadableStream<Uint8Array> {
  return new ReadableStream({
    start(controller) {
      controller.enqueue(data)
      controller.close()
    },
  })
}

async function compress(data: string): Promise<Uint8Array> {
  const encoder = new TextEncoder()
  const stream = byteStream(encoder.encode(data)).pipeThrough(new CompressionStream('deflate'))
  const reader = stream.getReader()
  const chunks: Uint8Array[] = []
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
  }
  const total = chunks.reduce((s, c) => s + c.length, 0)
  const result = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    result.set(chunk, offset)
    offset += chunk.length
  }
  return result
}

async function decompress(data: Uint8Array): Promise<string> {
  if (data.length > MAX_COMPRESSED_BYTES) throw new Error('Share payload is too large')
  const stream = byteStream(data).pipeThrough(new DecompressionStream('deflate'))
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let result = ''
  let expandedBytes = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    expandedBytes += value.length
    if (expandedBytes > MAX_EXPANDED_BYTES) {
      await reader.cancel()
      throw new Error('Share payload is too large')
    }
    result += decoder.decode(value, { stream: true })
  }
  result += decoder.decode()
  return result
}

// ─── Base64url encode/decode ───

function toBase64url(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64url(str: string): Uint8Array {
  if (str.length > MAX_ENCODED_LENGTH || !/^[A-Za-z0-9_-]+$/.test(str)) {
    throw new Error('Invalid share payload')
  }
  const padded = str.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (str.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

// ─── Public API ───

/**
 * Encode any JSON-serializable data into a compressed, URL-safe string.
 * Used for both layout and profile sharing.
 */
export async function encodeShareData(data: unknown): Promise<string> {
  const json = JSON.stringify(data)
  const compressed = await compress(json)
  return toBase64url(compressed)
}

/**
 * Decode a compressed, URL-safe string back into parsed JSON.
 */
export async function decodeShareData(encoded: string): Promise<unknown> {
  const bytes = fromBase64url(encoded)
  const json = await decompress(bytes)
  return JSON.parse(json)
}

interface ResolvedShare {
  kind: 'layout' | 'profile' | 'backtest-strategy'
  scope: ShareScope | 'backtest'
  schemaVersion: 2
  payloadEncoding: 'deflate-base64url'
  payload: string
}

/** Decode either a legacy fragment payload or a new registry-backed short link. */
export async function decodeShareLink(linkOrFragment: string): Promise<unknown> {
  const hashIndex = linkOrFragment.indexOf('#')
  const fragment = hashIndex === -1 ? linkOrFragment : linkOrFragment.slice(hashIndex + 1)
  if (!fragment) throw new Error('No share data found')
  if (!fragment.startsWith('s=')) return decodeShareData(fragment)

  const token = fragment.slice(2)
  if (!SHARE_TOKEN_PATTERN.test(token)) throw new Error('Invalid share token')
  const share = await usersClient.post<ResolvedShare>('/v2/shares/resolve', { token })
  if (share.payloadEncoding !== 'deflate-base64url') throw new Error('Unsupported share encoding')
  const decoded = await decodeShareData(share.payload)
  const envelope = decoded as { exportType?: unknown; scope?: unknown; version?: unknown }
  if (
    envelope.exportType !== share.kind ||
    envelope.scope !== share.scope ||
    envelope.version !== share.schemaVersion
  ) {
    throw new Error('Invalid share payload')
  }
  return decoded
}

/**
 * Build a LayoutExport payload from a workspace document.
 */
export function buildLayoutExport(
  scope: ShareScope,
  name: string,
  doc: WorkspaceDocument,
): LayoutExport {
  return {
    exportType: 'layout',
    scope,
    version: 2,
    name,
    mosaicTree: doc.mosaicTree,
    windowConfigs: doc.windowConfigs,
    panels: doc.panels,
    globalSettings: doc.globalSettings,
    ...(doc.tabGroups && Object.keys(doc.tabGroups).length > 0 ? { tabGroups: doc.tabGroups } : {}),
    // Grid (scroll) layouts keep their structure in gridLayout with a null
    // tree — omitting these fields would export an EMPTY layout (total panel
    // loss on import).
    ...(doc.layoutMode === 'grid'
      ? { layoutMode: 'grid' as const, gridLayout: doc.gridLayout ?? [] }
      : {}),
    exportedAt: new Date().toISOString(),
  }
}

/**
 * Build a ProfileExport payload.
 */
export function buildProfileExport(
  scope: ShareScope,
  name: string,
  profileType: string,
  data: Record<string, unknown>,
  subPanels?: Record<string, Record<string, unknown>>,
): ProfileExport {
  return {
    exportType: 'profile',
    scope,
    version: 2,
    profileType,
    name,
    data,
    ...(subPanels && Object.keys(subPanels).length > 0 ? { subPanels } : {}),
    exportedAt: new Date().toISOString(),
  }
}

/** Build the one pending-import shape used by route and pasted-link flows. */
export function buildPanelImportPayload(profile: ProfileExport, targetLayoutId: string) {
  const { panelId: _, componentType: __, ...panelState } = profile.data
  return {
    targetScope: profile.scope,
    targetLayoutId,
    componentType: profile.profileType,
    panelState,
    ...(profile.subPanels ? { subPanels: profile.subPanels } : {}),
    name: profile.name,
  }
}

/**
 * Generate a short share URL for an importable snapshot.
 * Only the registry token is placed in the URL hash fragment.
 */
export async function generateShareUrl(
  data: LayoutExport | ProfileExport | BacktestStrategyExport,
): Promise<string> {
  const encoded = await encodeShareData(data)
  const { token } = await usersClient.post<{ token: string }>('/v2/shares', {
    kind: data.exportType,
    scope: data.scope,
    schemaVersion: data.version,
    payloadEncoding: 'deflate-base64url',
    payload: encoded,
  })
  if (!SHARE_TOKEN_PATTERN.test(token)) throw new Error('Invalid share token')
  return `${window.location.origin}/import#s=${encodeURIComponent(token)}`
}

/** One import shape for the route and pasted-link flows. */
export function buildLayoutImportPayload(
  layout: LayoutExport,
): Omit<LayoutExport, 'exportType' | 'version' | 'exportedAt'> {
  return {
    name: layout.name,
    mosaicTree: layout.mosaicTree,
    windowConfigs: layout.windowConfigs,
    panels: layout.panels,
    scope: layout.scope,
    globalSettings: layout.globalSettings,
    ...(layout.tabGroups ? { tabGroups: layout.tabGroups } : {}),
    ...(layout.layoutMode === 'grid'
      ? { layoutMode: 'grid' as const, gridLayout: layout.gridLayout ?? [] }
      : {}),
  }
}

export function shareRoute(scope: ShareScope): '/dashboard' | '/history' {
  return scope === 'history' ? '/history' : '/dashboard'
}

/**
 * Validate and parse a layout import from decoded share data.
 */
export function validateLayoutImport(data: unknown): LayoutExport | null {
  const result = LayoutExportSchema.safeParse(data)
  return result.success ? result.data : null
}

/**
 * Validate and parse a profile import from decoded share data.
 */
export function validateProfileImport(data: unknown): ProfileExport | null {
  const result = ProfileExportSchema.safeParse(data)
  return result.success ? result.data : null
}

// ─── Preset export (dev tooling) ───

/** Separator in a chart grid cell id, `<panelId>::<cellIdx>`. */
const CELL_ID_SEPARATOR = '::'

/**
 * Transform a live workspace document into built-in-preset JSON
 * (features/dashboard/presets/*.json shape): stable readable panel ids,
 * transient runtime keys stripped, and only panels reachable from the tree
 * (leaves + their tab-group members) included. Stale tab groups whose host is
 * no longer a tree leaf are dropped.
 *
 * Dev workflow: right-click a layout tab -> "Copy as preset JSON" (dev builds
 * only) -> paste into a new file in features/dashboard/presets/ and register
 * it in presets/index.ts.
 */
export function buildPresetExport(name: string, doc: WorkspaceDocument): Record<string, unknown> {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

  // Collect reachable panel ids: tree leaves, then members of tab groups
  // hosted by a leaf.
  const leaves: string[] = []
  const walkLeaves = (node: unknown): void => {
    if (typeof node === 'string') {
      leaves.push(node)
      return
    }
    if (node && typeof node === 'object') {
      const n = node as { first?: unknown; second?: unknown }
      walkLeaves(n.first)
      walkLeaves(n.second)
    }
  }
  walkLeaves(doc.mosaicTree)

  const liveGroups = Object.entries(doc.tabGroups ?? {}).filter(([hostId]) =>
    leaves.includes(hostId),
  )
  // A tab member with no windowConfig is a leftover of a panel that is gone.
  // windowConfigs below already skips it; carrying it in tabGroups anyway is
  // what shipped dangling "preset_*_panel" ids into the first real export.
  const isConfigured = (id: string) => doc.windowConfigs[id] != null
  const reachable = [...leaves]
  for (const [, group] of liveGroups) {
    for (const tabId of group.tabs) {
      if (isConfigured(tabId) && !reachable.includes(tabId)) reachable.push(tabId)
    }
  }

  // Stable readable ids: preset_<slug>_<componentType>, numbered on repeats.
  const idMap = new Map<string, string>()
  const used = new Set<string>()
  for (const oldId of reachable) {
    const type = (doc.windowConfigs[oldId]?.componentType ?? 'panel').replace(/[^a-z0-9]+/g, '_')
    let candidate = `preset_${slug}_${type}`
    let n = 2
    while (used.has(candidate)) candidate = `preset_${slug}_${type}_${n++}`
    used.add(candidate)
    idMap.set(oldId, candidate)
  }
  /**
   * Chart grid cells are keyed `<panelId>::<cellIdx>` and are not panels in
   * their own right: no windowConfig, no tree leaf, no tab membership. Their id
   * has to follow the host panel's new id so the launched preset's grid finds
   * them again.
   */
  const remap = (oldId: string): string => {
    const sep = oldId.indexOf(CELL_ID_SEPARATOR)
    if (sep !== -1) return `${remap(oldId.slice(0, sep))}${oldId.slice(sep)}`
    return idMap.get(oldId) ?? oldId
  }

  const mapTree = (node: unknown): unknown => {
    if (typeof node === 'string') return remap(node)
    const n = node as { first: unknown; second: unknown }
    return { ...(node as object), first: mapTree(n.first), second: mapTree(n.second) }
  }

  // symbolIndex addresses the AUTHOR's symbol list, so an exported 569 points at
  // a different ticker (or none) for anyone else. Rebase from 0 in first-seen
  // order rather than flattening to 0: which panels share a ticker, and which
  // deliberately do not, is real layout intent and has to survive the trip.
  const symbolIndexMap = new Map<number, number>()
  const rebaseSymbolIndex = (index: number): number => {
    let next = symbolIndexMap.get(index)
    if (next === undefined) {
      next = symbolIndexMap.size
      symbolIndexMap.set(index, next)
    }
    return next
  }

  const windowConfigs: Record<string, unknown> = {}
  for (const oldId of reachable) {
    const config = doc.windowConfigs[oldId]
    if (!config) continue
    // tickerCleared is transient runtime state, not preset content.
    const { tickerCleared: _tickerCleared, ...rest } = config as Record<string, unknown>
    if (typeof rest.symbolIndex === 'number') rest.symbolIndex = rebaseSymbolIndex(rest.symbolIndex)
    windowConfigs[remap(oldId)] = rest
  }

  const panels: Record<string, unknown> = {}
  const reachableSet = new Set(reachable)
  for (const [oldId, state] of Object.entries(doc.panels ?? {})) {
    if (!state || Object.keys(state).length === 0) continue
    // A cell rides along with its host panel; anything else must be reachable
    // in its own right.
    const sep = oldId.indexOf(CELL_ID_SEPARATOR)
    const owner = sep === -1 ? oldId : oldId.slice(0, sep)
    if (!reachableSet.has(owner)) continue
    panels[remap(oldId)] = state
  }

  const tabGroups: Record<string, unknown> = {}
  for (const [hostId, group] of liveGroups) {
    const tabs = group.tabs.filter(isConfigured).map(remap)
    // A group of one is not a group: once the leftovers are gone, the host is
    // just a panel again.
    if (tabs.length < 2) continue
    const activeTabId = remap(group.activeTabId)
    tabGroups[remap(hostId)] = {
      tabs,
      activeTabId: tabs.includes(activeTabId) ? activeTabId : tabs[0],
    }
  }

  return {
    presetId: `preset_${slug}`,
    name,
    description: '',
    category: 'Custom',
    requiredPlan: 'basic',
    mosaicTree: mapTree(doc.mosaicTree),
    windowConfigs,
    ...(Object.keys(tabGroups).length > 0 ? { tabGroups } : {}),
    ...(Object.keys(panels).length > 0 ? { panels } : {}),
    ...(doc.layoutMode === 'grid'
      ? { layoutMode: 'grid' as const, gridLayout: doc.gridLayout ?? [] }
      : {}),
  }
}
